import { Capacitor } from '@capacitor/core'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { cx } from 'class-variance-authority'
import cn from 'classnames'
import { type FC } from 'react'
import { useNavigate } from 'react-router'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'
import { Tag } from '../tag/tag'

export interface PageHeaderRedesignProps extends DefaultProps {
  /**
   * Header title
   */
  title?: string
  /**
   * Header subtitle/label
   */
  subtitle?: string
  /**
   * Back link url
   */
  backLinkUrl?: string
  /**
   * Title class name
   */
  titleClassName?: string
  /**
   * Subtitle class name
   */
  subtitleClassName?: string
  /**
   * On click back button
   */
  onClickBackButton?: () => void

  /**
   * Last Updated
   */
  lastRefreshed?: string
  /**
   * Container class name
   */
  containerClassName?: string
}

export const PageHeaderRedesign: FC<PageHeaderRedesignProps> = ({
  title,
  subtitle,
  backLinkUrl,
  className,
  id,
  testId,
  ariaLabel,
  titleClassName,
  subtitleClassName,
  lastRefreshed,
  onClickBackButton,
  containerClassName,
}) => {
  const navigate = useNavigate()

  const finalClasses = twMerge('egw-container mt-2xs lg:mt-4xl ', className)

  const subtitleClasses = twMerge('uppercase typo-primary-sm-regular text-text-body', subtitleClassName)

  const titleClasses = twMerge('text-text-body typo-primary-3xl-regular mt-3xs', titleClassName)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div
        className={cn('flex items-start gap-lg', containerClassName, {
          'pt-safe': Capacitor.isNativePlatform(),
        })}
      >
        {(backLinkUrl ?? onClickBackButton) ? (
          <button
            className="hidden lg:flex items-center justify-center w-8 h-8 transition-colors duration-200 group"
            id={BACK_LINK_ID}
            type="button"
            data-back-link-url={backLinkUrl}
            onClick={() => {
              if (backLinkUrl) {
                void navigate(backLinkUrl)
              } else {
                onClickBackButton?.()
              }
            }}
            aria-label="Go back"
          >
            <ChevronLeftIcon
              id="back_link_icon_redesign"
              className="w-5 h-5 stroke-icons-full group-hover:stroke-icons-muted"
            />
          </button>
        ) : null}

        <div className="flex flex-col flex-1 min-w-0">
          {subtitle ? <div className={subtitleClasses}>{subtitle}</div> : null}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end">
            {title ? (
              <h1 data-testid="page-header-redesign-title" className={titleClasses}>
                {title}
              </h1>
            ) : null}
            {lastRefreshed ? (
              <Tag size="large" className="text-text-meta bg-gray-50 mt-xs mb-0 lg:mt-0 border-0">
                <p className="typo-primary-sm-regular">{lastRefreshed}</p>
              </Tag>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

// PLACING IN A CONSTANT AS IT IS REFERENCED IN THE REDESIGN LAYOUT
export const BACK_LINK_ID = 'back-link-page-header-redesign'
